// JOBE-0044.14: operator-unreachable variant. Renders a --block-bordered
// banner above the cached rows. The cached rows render below with a
// `.cached` modifier on the ago cell ("3h · cached"). Banner copy
// includes the cache-entry timestamp.

import { GroupDivider } from '../GroupDivider.jsx';
import { LibraryRow } from '../LibraryRow.jsx';
import { groupLabel } from '../groupLabel.js';

function formatHHMM(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

export function UnreachableState({
  cachedRows,
  cachedAt,
  selectedUrn,
  onSelect,
  onChipAppend,
  onRetry,
}) {
  const rows = Array.isArray(cachedRows) ? cachedRows : [];
  const now = Date.now();
  const items = [];
  let prev = null;
  for (const row of rows) {
    const label = groupLabel(row.lastChangedAt, now);
    if (label !== prev) {
      items.push({ kind: 'divider', label, key: `div:${label}:${row.urn}` });
      prev = label;
    }
    items.push({ kind: 'row', row, key: row.urn });
  }

  const stamp = formatHHMM(cachedAt);

  return (
    <div
      className="library-state-unreachable"
      data-state="unreachable"
    >
      <div className="library-cached-banner" role="status">
        <span className="library-cached-banner-text">
          Can't reach the operator. Showing your last cached view
          {stamp ? ` (${stamp}).` : '.'}
        </span>
        {onRetry && (
          <button
            type="button"
            className="library-cached-banner-retry"
            onClick={onRetry}
          >
            Retry
          </button>
        )}
      </div>
      <div className="library-table-body library-table-body-cached">
        {items.map((it) =>
          it.kind === 'divider' ? (
            <GroupDivider key={it.key} label={it.label} />
          ) : (
            <CachedRowWrap
              key={it.key}
              row={it.row}
              selected={it.row.urn === selectedUrn}
              onSelect={onSelect}
              onChipAppend={onChipAppend}
            />
          ),
        )}
      </div>
    </div>
  );
}

// CachedRowWrap — renders LibraryRow with the .cached stamp overlaid.
// LibraryRow's outer element is already an <li>, so we render this
// wrapper as a Fragment-style group inside the parent <ul>, wrapping
// just the row + stamp in a positioned container that's not a <li>.
// To keep semantics correct, we use the row's own <li> as the list
// item and absolute-position the stamp relative to it.
function CachedRowWrap({ row, selected, onSelect, onChipAppend }) {
  return (
    <div className="library-row-cached-wrap" data-cached="true">
      <LibraryRow
        row={row}
        selected={selected}
        onSelect={onSelect}
        onChipAppend={onChipAppend}
      />
      <span
        className="library-row-cached-stamp"
        aria-label="cached"
      >
        · cached
      </span>
    </div>
  );
}
